import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Copy, Globe, Webhook, BarChart3, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminSettings() {
  const [form, setForm] = useState({ ga4_id: '', site_title: '', site_description: '', contact_email: '', contact_phone: '', instagram_url: '', looker_studio_url: '' });
  const [webhook, setWebhook] = useState(null);
  const [busy, setBusy] = useState(false);
  const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';

  useEffect(() => {
    api.get('/admin/settings').then((r) => setForm({
      ga4_id: r.data?.ga4_id || '', site_title: r.data?.site_title || '', site_description: r.data?.site_description || '',
      contact_email: r.data?.contact_email || '', contact_phone: r.data?.contact_phone || '', instagram_url: r.data?.instagram_url || '',
      looker_studio_url: r.data?.looker_studio_url || '',
    }));
    api.get('/admin/webhook-info').then((r) => setWebhook(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/admin/settings', form);
      toast.success('Impostazioni salvate');
    } catch (err) { toast.error('Errore'); }
    finally { setBusy(false); }
  };

  const copy = (text, label = 'Copiato') => {
    navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error('Impossibile copiare'));
  };

  return (
    <AdminLayout title="Impostazioni">
      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Globe} title="SEO & Sito">
          <Field label="Titolo del sito"><Input value={form.site_title} onChange={(e) => setForm({...form, site_title: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
          <Field label="Meta description (SEO globale)"><Textarea rows={3} value={form.site_description} onChange={(e) => setForm({...form, site_description: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
          <Field label="Email di contatto"><Input type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
          <Field label="Telefono"><Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
          <Field label="Instagram URL"><Input value={form.instagram_url} onChange={(e) => setForm({...form, instagram_url: e.target.value})} placeholder="https://instagram.com/…" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
        </Section>

        <Section icon={BarChart3} title="Google Analytics 4">
          <Field label="GA4 Measurement ID (es. G-XXXXXXX)">
            <Input data-testid="settings-ga4-input" value={form.ga4_id} onChange={(e) => setForm({...form, ga4_id: e.target.value})} placeholder="G-XXXXXXX" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/>
          </Field>
          <p className="text-xs text-[color:var(--text-muted)]">Quando inserisci il codice, GA4 verrà caricato automaticamente sulle pagine pubbliche. Eventi tracciati: <code className="text-[color:var(--gold)]">click_acquista</code>, <code className="text-[color:var(--gold)]">click_tavolo</code>.</p>
        </Section>

        <Section icon={TrendingUp} title="Looker Studio (Statistiche Traffico)">
          <Field label="URL di embed del report Looker Studio">
            <Input data-testid="settings-looker-input" value={form.looker_studio_url} onChange={(e) => setForm({...form, looker_studio_url: e.target.value})} placeholder="https://lookerstudio.google.com/embed/reporting/..." className="bg-[color:var(--surface-2)] border-[color:var(--border-c)] font-mono text-xs"/>
          </Field>
          <p className="text-xs text-[color:var(--text-muted)]">Su Looker Studio: <b>Condividi → Incorpora report → Abilita</b>, poi copia l'URL dal codice iframe (campo <code className="text-[color:var(--gold)]">src</code>). Il grafico apparirà nella sezione <a href="/admin/traffico" className="text-[color:var(--gold)] hover:underline">Statistiche Traffico</a>.</p>
        </Section>

        <Section icon={Webhook} title="Webhook API (Zapier / Make.com)" wide>
          <p className="text-sm text-[color:var(--text-muted)]">Usa questi endpoint per creare/aggiornare eventi via webhook. Devi includere l'header <code className="text-[color:var(--gold)]">{webhook?.header_name || 'X-API-Key'}</code> con la chiave configurata sul server (variabile <code>WEBHOOK_API_KEY</code>).</p>
          <CopyRow label="POST Crea Evento" value={`${baseUrl}${webhook?.webhook_create_url || '/api/webhooks/events'}`} testId="settings-webhook-create"/>
          <CopyRow label="PATCH Aggiorna Evento" value={`${baseUrl}${(webhook?.webhook_update_url || '/api/webhooks/events/{id}')}`} testId="settings-webhook-update"/>
          <CopyRow label="Header Name" value={webhook?.header_name || 'X-API-Key'} testId="settings-webhook-header"/>
          <CopyRow label="API Key (preview)" value={webhook?.api_key_preview || ''} testId="settings-webhook-key-preview" disabled />
          <details className="text-xs text-[color:var(--text-muted)]">
            <summary className="cursor-pointer text-[color:var(--gold)]">Esempio payload POST</summary>
            <pre className="mt-2 p-3 rounded bg-[color:var(--bg-2)] border border-[color:var(--border-c)] overflow-x-auto">{JSON.stringify({
              titolo: "Saturday Night",
              data: "2025-12-31",
              orario: "23:00",
              locale_id: "<id-del-locale>",
              zona: "Testaccio",
              descrizione: "Speciale Capodanno",
              link_acquisto: "https://tickets.example.com/123",
              tavolo_prive: true,
              link_tavolo: "https://reservation.example.com/123",
              stato_revisione: "In Revisione",
              in_evidenza: true
            }, null, 2)}</pre>
          </details>
        </Section>
      </div>

      <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 border-t border-[color:var(--border-c)] bg-[rgba(11,11,12,0.92)] backdrop-blur flex justify-end">
        <Button data-testid="settings-save" onClick={save} disabled={busy} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold"><Save className="w-4 h-4 mr-2"/>{busy ? 'Salvataggio…' : 'Salva Impostazioni'}</Button>
      </div>
    </AdminLayout>
  );
}

function Section({ icon: Icon, title, children, wide }) {
  return (
    <div className={`rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5 space-y-3 ${wide ? 'lg:col-span-2' : ''}`}>
      <h3 className="font-heading text-lg flex items-center gap-2 text-[color:var(--gold)] mb-1"><Icon className="w-4 h-4"/>{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (<div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{label}</Label>{children}</div>);
}
function CopyRow({ label, value, testId, disabled }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{label}</Label>
      <div className="flex gap-2">
        <Input data-testid={testId} value={value} readOnly className="bg-[color:var(--surface-2)] border-[color:var(--border-c)] font-mono text-xs"/>
        {!disabled && (<Button type="button" size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(value).then(() => toast.success('Copiato'))} ><Copy className="w-4 h-4"/></Button>)}
      </div>
    </div>
  );
}
